import React from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

export interface RangePreviewFrame {
  id: string;
  src: string;
  sourceTimeSec: number;
  sourceId?: string;
  sourceDurationSec?: number;
  width?: number | null;
  height?: number | null;
}

export const RANGE_PREVIEW_TRACK_HEIGHT = 80;
export const RANGE_PREVIEW_FRAME_COUNT = 24;
export const RANGE_PREVIEW_FRAME_STATE = {
  LOADING: 'loading',
  READY: 'ready',
  FAILED: 'failed',
} as const;
export type RangePreviewFrameLoadState = typeof RANGE_PREVIEW_FRAME_STATE[keyof typeof RANGE_PREVIEW_FRAME_STATE];

// A single mid-clip thumbnail frame, captured/cached from the transcoded preview. Lives here
// beside the filmstrip builder rather than in a clip grid, because two grids want it now: the
// editor's clip browser and the library's Add clips tab.
export function sourceThumbFrame(
  clip: { fileId: string; streamUrl: string; durationSec?: number | null; width?: number | null; height?: number | null },
): RangePreviewFrame {
  return {
    id: `${clip.fileId}:thumb`,
    src: clip.streamUrl,
    sourceTimeSec: Math.max(0.1, (clip.durationSec || 2) / 2),
    sourceId: clip.fileId,
    sourceDurationSec: clip.durationSec ?? undefined,
    width: clip.width,
    height: clip.height,
  };
}

export function sourceRangePreviewFrames(
  sourceId: string,
  src: string,
  duration: number,
  width?: number | null,
  height?: number | null,
): RangePreviewFrame[] {
  if (duration <= 0) return [];
  return Array.from({ length: RANGE_PREVIEW_FRAME_COUNT }, (_, index) => {
    const sourceTimeSec = ((index + 0.5) / RANGE_PREVIEW_FRAME_COUNT) * duration;
    return {
      id: `${sourceId}:${sourceTimeSec.toFixed(2)}`,
      src,
      sourceTimeSec,
      sourceId,
      sourceDurationSec: duration,
      width,
      height,
    };
  });
}

const STORAGE_PREFIX = 'sbly-tools:video-edit-range-frame:v5:';
const THUMBNAIL_MAX_EDGE = 360;
const THUMBNAIL_QUALITY = 0.62;
const SOURCE_CACHE_BYTES = 16 * 1024 * 1024;
// Time to get the video track + FIRST decoded frame from a source before we give
// up on it. Kept short so a clip whose container/network stalls doesn't hold the
// capture slot (and freeze the strip) for long — a failure then retries below.
const CAPTURE_STARTUP_TIMEOUT_MS = 7000;
// Once frames are flowing, allow this long between frames before treating the
// pipeline as hung (a large batch that keeps yielding is fine).
const CAPTURE_PROGRESS_TIMEOUT_MS = 12000;
// How many clips decode at once. More than one so a slow or stuck clip can't fully
// block another (switching clips, or the aligned editor's two video lanes loading
// together); bounded because each concurrent source holds its own <video> streaming
// and seeking a real file.
//
// Raised from 2 to 4: a folder's clips resolved visibly one pair at a time, and the
// wait is dominated by network round trips rather than CPU, so more of them in flight
// is close to a straight speedup. Kept under Chrome's ~6-connections-per-host limit —
// past that the captures queue at the socket instead, trading a visible queue for an
// invisible one while still paying the decode cost.
const MAX_CONCURRENT_SOURCE_CAPTURES = 4;
// A transient stall (network/decode) fails a frame; retry a couple of times with
// backoff so it self-heals instead of leaving the thumbnail dark until reselect.
const MAX_FRAME_RETRIES = 2;
const FRAME_RETRY_DELAY_MS = 1500;

const memoryCache = new Map<string, string>();
const pendingCaptures = new Map<string, Promise<string | null>>();
interface CaptureRequest {
  frame: RangePreviewFrame;
  resolve: (value: string | null) => void;
}
interface SourceCaptureQueue {
  src: string;
  requests: CaptureRequest[];
  queued: boolean;
  active: boolean;
}
const sourceCaptureQueues = new Map<string, SourceCaptureQueue>();
const queuedSources: SourceCaptureQueue[] = [];
let activeSourceCaptures = 0;

function stableHash(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function rangePreviewFrameStorageKey(frame: RangePreviewFrame) {
  return `${STORAGE_PREFIX}${stableHash(`${frame.id}:${frame.sourceTimeSec.toFixed(2)}`)}`;
}

function readCachedFrame(frame: RangePreviewFrame) {
  const key = rangePreviewFrameStorageKey(frame);
  const memoryValue = memoryCache.get(key);
  if (memoryValue) return memoryValue;
  try {
    const storedValue = sessionStorage.getItem(key);
    if (storedValue) {
      memoryCache.set(key, storedValue);
      return storedValue;
    }
  } catch { /* session cache is an optimization only */ }
  return null;
}

function saveCachedFrame(frame: RangePreviewFrame, imageUrl: string) {
  const key = rangePreviewFrameStorageKey(frame);
  memoryCache.set(key, imageUrl);
  try { sessionStorage.setItem(key, imageUrl); } catch { /* memory cache still works */ }
}

async function captureSourceQueue(queue: SourceCaptureQueue) {
  let activeBatch: CaptureRequest[] = [];
  let input: { disposed: boolean; dispose: () => void } | null = null;
  let timeout: number | undefined;
  try {
    // MediaBunny reads only the byte ranges needed for these timestamps and
    // decodes the sorted batch through one WebCodecs pipeline. This avoids the
    // unreliable hidden <video> seek events that previously stalled forever.
    const { ALL_FORMATS, CanvasSink, Input, UrlSource } = await import('mediabunny');
    const source = new UrlSource(queue.src, {
      maxCacheSize: SOURCE_CACHE_BYTES,
      // Keep thumbnail traffic behind visible playback even after the player is
      // ready. Browsers that do not recognize fetch priority safely ignore it.
      requestInit: { priority: 'low' } as RequestInit,
      parallelism: 1,
      getRetryDelay: (previousAttempts) => (previousAttempts < 1 ? 250 : null),
    });
    const mediaInput = new Input({ formats: ALL_FORMATS, source });
    input = mediaInput;
    // Watchdog, not a fixed deadline: it fires only after a stretch with NO
    // progress. Short until the first frame (so a stuck source releases the slot
    // fast), then generous between frames so a large batch that keeps yielding
    // is allowed to finish.
    const bumpWatchdog = (ms: number) => {
      if (timeout != null) window.clearTimeout(timeout);
      timeout = window.setTimeout(() => mediaInput.dispose(), ms);
    };
    bumpWatchdog(CAPTURE_STARTUP_TIMEOUT_MS);
    const videoTrack = await mediaInput.getPrimaryVideoTrack();
    if (!videoTrack || !(await videoTrack.canDecode())) throw new Error('Video track cannot be decoded in this browser');

    const firstFrame = queue.requests[0]?.frame;
    const portrait = !!firstFrame?.width && !!firstFrame.height && firstFrame.height > firstFrame.width;
    const sink = new CanvasSink(videoTrack, portrait
      ? { height: THUMBNAIL_MAX_EDGE }
      : { width: THUMBNAIL_MAX_EDGE });

    while (queue.requests.length > 0) {
      activeBatch = queue.requests.splice(0).sort((a, b) => a.frame.sourceTimeSec - b.frame.sourceTimeSec);
      let index = 0;
      const timestamps = activeBatch.map(({ frame }) => frame.sourceTimeSec);
      for await (const result of sink.canvasesAtTimestamps(timestamps)) {
        bumpWatchdog(CAPTURE_PROGRESS_TIMEOUT_MS);
        const request = activeBatch[index];
        index += 1;
        if (!request) continue;
        let imageUrl: string | null = null;
        if (result && result.canvas instanceof HTMLCanvasElement) {
          imageUrl = result.canvas.toDataURL('image/jpeg', THUMBNAIL_QUALITY);
        }
        request.resolve(imageUrl);
      }
      activeBatch.slice(index).forEach(({ resolve }) => resolve(null));
      activeBatch = [];
    }
  } catch (error) {
    const rawMessage = error instanceof Error ? error.message : String(error);
    const safeMessage = rawMessage.split(queue.src).join('[media source]');
    console.warn('Unable to generate video scrubber thumbnails:', safeMessage);
    activeBatch.forEach(({ resolve }) => resolve(null));
    queue.requests.splice(0).forEach(({ resolve }) => resolve(null));
  } finally {
    if (timeout != null) window.clearTimeout(timeout);
    if (input && !input.disposed) input.dispose();
  }
}

function pumpSourceCaptureQueues() {
  if (activeSourceCaptures >= MAX_CONCURRENT_SOURCE_CAPTURES) return;
  const queue = queuedSources.shift();
  if (!queue) return;
  if (queue.active || queue.requests.length === 0) {
    pumpSourceCaptureQueues();
    return;
  }
  queue.queued = false;
  queue.active = true;
  activeSourceCaptures += 1;
  captureSourceQueue(queue).finally(() => {
    queue.active = false;
    activeSourceCaptures -= 1;
    if (queue.requests.length > 0) {
      queue.queued = true;
      queuedSources.push(queue);
    } else {
      sourceCaptureQueues.delete(queue.src);
    }
    pumpSourceCaptureQueues();
  });
}

function enqueueCapture(frame: RangePreviewFrame) {
  return new Promise<string | null>((resolve) => {
    let queue = sourceCaptureQueues.get(frame.src);
    if (!queue) {
      queue = { src: frame.src, requests: [], queued: false, active: false };
      sourceCaptureQueues.set(frame.src, queue);
    }
    queue.requests.push({ frame, resolve });
    if (!queue.active && !queue.queued) {
      queue.queued = true;
      queuedSources.push(queue);
    }
    pumpSourceCaptureQueues();
  });
}

function loadCachedFrame(frame: RangePreviewFrame) {
  const cached = readCachedFrame(frame);
  if (cached) return Promise.resolve(cached);
  const key = rangePreviewFrameStorageKey(frame);
  const pending = pendingCaptures.get(key);
  if (pending) return pending;
  const request = enqueueCapture(frame).then((imageUrl) => {
    pendingCaptures.delete(key);
    if (imageUrl) saveCachedFrame(frame, imageUrl);
    return imageUrl;
  });
  pendingCaptures.set(key, request);
  return request;
}

interface CachedRangePreviewFrameProps {
  frame: RangePreviewFrame;
  loadEnabled?: boolean;
  onLoadStateChange?: (frameId: string, state: RangePreviewFrameLoadState) => void;
}

export default function CachedRangePreviewFrame({ frame, loadEnabled = true, onLoadStateChange }: CachedRangePreviewFrameProps) {
  const [imageUrl, setImageUrl] = React.useState<string | false | null>(() => readCachedFrame(frame));
  const retriesRef = React.useRef(0);

  React.useEffect(() => {
    retriesRef.current = 0;
    setImageUrl(readCachedFrame(frame));
  }, [frame]);

  React.useEffect(() => {
    if (!loadEnabled || imageUrl !== null) return undefined;
    let active = true;
    loadCachedFrame(frame).then((value) => {
      if (active) setImageUrl(value || false);
    });
    return () => { active = false; };
  }, [frame, imageUrl, loadEnabled]);

  // Retry a failed capture a couple of times with backoff — a transient stall
  // (a busy capture slot, a slow byte-range fetch) then recovers on its own
  // instead of leaving the cell dark until the clip is reselected.
  React.useEffect(() => {
    if (imageUrl !== false || !loadEnabled || retriesRef.current >= MAX_FRAME_RETRIES) return undefined;
    const attempt = retriesRef.current + 1;
    const timer = window.setTimeout(() => {
      retriesRef.current = attempt;
      setImageUrl(null); // re-runs the load effect with a fresh capture request
    }, FRAME_RETRY_DELAY_MS * attempt);
    return () => window.clearTimeout(timer);
  }, [imageUrl, loadEnabled]);

  React.useEffect(() => {
    let state: RangePreviewFrameLoadState = RANGE_PREVIEW_FRAME_STATE.LOADING;
    if (typeof imageUrl === 'string') state = RANGE_PREVIEW_FRAME_STATE.READY;
    else if (imageUrl === false) state = RANGE_PREVIEW_FRAME_STATE.FAILED;
    onLoadStateChange?.(frame.id, state);
  }, [frame.id, imageUrl, onLoadStateChange]);

  // A frame that has given up. Flat and static on purpose: an animation here would
  // promise a capture that is no longer coming, the same reason a failed transcode
  // gets a marker rather than a spinner.
  if (imageUrl === false) {
    return (
      <Box
        data-testid="range-trimmer-video-frame"
        data-source-time={frame.sourceTimeSec}
        data-source-aspect={frame.width && frame.height ? `${frame.width}:${frame.height}` : undefined}
        data-thumbnail-state="fallback"
        aria-hidden="true"
        sx={{ width: '100%', height: '100%', backgroundColor: '#334155' }}
      />
    );
  }

  // Still capturing. Thumbnails are produced in the BROWSER — byte ranges of the MP4
  // are fetched and decoded through WebCodecs, at deliberately low fetch priority so
  // they stay behind visible playback — so there is a real wait after the transcode is
  // done, paid once per clip before the cache warms. A flat dark box for that wait is
  // indistinguishable from a dark video frame, which is why the whole thing read as
  // "nothing is happening". A wave says it is working without adding a spinner to
  // every cell of every filmstrip.
  //
  // One placeholder covers all three surfaces, since the clip rail, the timeline
  // filmstrip and the range trimmer all render this component.
  if (!imageUrl) {
    return (
      <Skeleton
        variant="rectangular"
        animation="wave"
        data-testid="range-trimmer-video-frame"
        data-source-time={frame.sourceTimeSec}
        data-source-aspect={frame.width && frame.height ? `${frame.width}:${frame.height}` : undefined}
        data-thumbnail-state="loading"
        aria-hidden="true"
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: '#334155',
          // The default wave is tuned for light surfaces and vanishes on this one.
          '&::after': { background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.16), transparent)' },
        }}
      />
    );
  }

  return (
    <Box
      data-testid="range-trimmer-video-frame"
      data-source-time={frame.sourceTimeSec}
      data-source-aspect={frame.width && frame.height ? `${frame.width}:${frame.height}` : undefined}
      data-thumbnail-state="ready"
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#334155',
        backgroundImage: `url(${imageUrl})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    />
  );
}
